import { Badge, Box, Card, Divider, FormGroup, MenuItem, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { ChangeEvent, Dispatch, FC, useEffect, useRef, useState } from "react";
import { BasicInfoPiece, BasicInfoRow } from "../backups/BackupBasicInfoCard";
import { DatabaseResponseDto, TableSchemaResponseDto, TableSemanticsStatisDto } from "@/gen/api/governance";
import { crudGetList, ReduxState } from "ra-core";
import { useDispatch, useSelector } from "react-redux";
import { RecPatch } from "@/dataProvider/tools";
import { push } from "connected-react-router";
import { genUrlWithHashid } from "@/utils/hashid";
import { pages, sorts } from "@/utils/paramsWithGetMainList";



const tableSemanticRes = 'governance/table-semantic';
const tableSemanticStatisRes = 'governance/table-semantic-statis';

const DatabaseTableSemanticInfoCard: FC<{
  database : DatabaseResponseDto,
  table: TableSchemaResponseDto,
}> = ({
  database, table,
}) => {
  const dbTables = useSelector<ReduxState, (TableSchemaResponseDto&RecPatch)[]|null>(store => {
    const res = store.admin.resources[tableSemanticRes]
    if (!res) { return null }
    const tables = (res.list.ids ?? []).map(id => res.data[id]) as any as (TableSchemaResponseDto&RecPatch)[]

    return tables.sort((a, b) =>
      a.schema === b.schema 
      ? ((a.name ?? '') < (b.name ?? '') ? -1 : 1)
      : ((a.schema ?? '') < (b.schema ?? '') ? -1 : 1)
    )
  })
  const [states, setStates] = useState({
    currTable: table as TableSchemaResponseDto&RecPatch
  })
  useEffect(() => {
    setStates(v => {
      if ((table as any).id !== v.currTable.id) {
        return {...v, currTable: table as any}
      }
      return v
    })
  }, [table])

  const dispatch = useDispatch()

  useFetchTableSemanticsStatis(dbTables, database, dispatch);
  const statisInfo = useSelector<ReduxState, Record<string, TableSemanticsStatisDto>>(store => {
    return (store.admin.resources[tableSemanticStatisRes]?.data ?? {}) as any
  })

  const handleTableChange = (e: ChangeEvent<HTMLInputElement>) => {
    setStates(v => {
      const selectedId = e.target.value
      if (v.currTable.id === selectedId) { return v }
      const targetTable = dbTables?.find(x => x.id === selectedId)
      if (!targetTable) { return v }
      setTimeout(() => {
        const dbHashId = genUrlWithHashid("$0", database.databaseId ?? 0, database.name ?? '');
        const tableHashId = genUrlWithHashid("$0", targetTable.tableId ?? 0, targetTable.name ?? '');
        // Navigate to table fields edit page
        dispatch(push(`/governance/database-semantic/${dbHashId}/table-fields/${tableHashId}/edit`));
      })
      return {...v, currTable: targetTable}
    })
  }

  return (
    <Card>
      <BasicInfoRow>
        <BasicInfoPiece>
          <Typography color="textPrimary" variant="h6" >
            Database
          </Typography>
          <Typography color="textSecondary" variant="body1" >
            {database.name}
          </Typography>
        </BasicInfoPiece>
        <BasicInfoPiece>
          <Typography color="textPrimary" variant="h6" >
            Engine
          </Typography>
          <Typography color="textSecondary" variant="body1" >
            {database?.engineVersion + ' ' + (database?.engineEdition || '')}
          </Typography>
        </BasicInfoPiece>
      </BasicInfoRow>
      <Divider />
      <Stack direction="row" sx={{ mx: 3, my: 2 }} spacing={4}  >        
        <Typography color="textPrimary" variant="h6"
          id="database-comment-label" 
          sx={{ width: 90 }}
        >
          Table
        </Typography>
        <Stack direction="row" flex="1 1 0" alignItems="center" sx={listSx}>
          {!dbTables
          ? <Typography variant="body2" color="textPrimary" >
              <Typography variant="caption" color="textSecondary" >
                {table?.schema}.
              </Typography>
              {" "}{table?.name}
            </Typography>
          : <FormGroup sx={{ width: '100%' }}>
              <TextField select size="small" id='table-select' fullWidth 
                value={states.currTable?.id || dbTables[0]?.id || ''}
                onChange={handleTableChange}
                SelectProps={{
                  inputProps: {
                    labelId: 'table-name',
                    'aria-label': "Select Table",
                  },
                  MenuProps: { PaperProps: { sx: listSx }, keepMounted: true },
                }}
              >
                {dbTables.map(t => {
                  const bag : TableSemanticsStatisDto|undefined = statisInfo[t.id]
                  return (
                    <MenuItem key={t.id} value={t.id}>
                      <Tooltip title={`${t.schema}.${t.name}`}>
                        <Box className="gcontainer"
                          sx={{ 
                            width: '100%',
                            '& span.MuiTypography-body2': { 
                              maxWidth: 320,
                              wordBreak: 'keep-all',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              mr: 'auto',
                            }
                          }}
                        >
                          <Typography component="span" variant="caption" color="InactiveCaptionText"
                            className="i1"
                          >
                            {t.schema}.
                          </Typography>
                          <Typography component="span" variant="body2" 
                            className="i2"                        
                          >
                            {t.name}
                          </Typography>
                          <Typography component="span" variant="caption" color="error.main"
                            className="i3"
                          >
                            <Badge 
                              color={"success" as any} 
                              badgeContent={(bag?.totalFields ?? 0) - (bag?.emptySemantic ?? 0)} 
                              // showZero 
                              max={9999} 
                            />
                          </Typography>
                          <Typography component="span" variant="caption"
                            className="i4"
                            sx={{ color: 'warning.light', mr: 2 }}
                          >
                            <Badge 
                              color={"warning" as any} 
                              badgeContent={bag?.emptySemantic} 
                              // showZero 
                              max={9999} 
                              sx={{ 'span.MuiBadge-badge': {color: theme => theme.palette.mode === 'light' ? 'black!important' : undefined }}}
                            />
                          </Typography>
                        </Box>
                      </Tooltip>
                    </MenuItem>
                  )
                })}
              </TextField>
            </FormGroup>
          }
        </Stack>
      </Stack>
    </Card>
  )
}

export default DatabaseTableSemanticInfoCard;


const listSx = {
  '& .gcontainer': {                          
    display: 'grid',
    gridAutoColumns: 'minmax(50px, auto) 1fr minmax(20px, auto) minmax(20px, auto)',
    columnGap: 0.5,
    '& .i1': {
      lineHeight: '1.8',
      textAlign: 'right',
      gridColumn: '1 / span 1'
    },
    '& .i2': { gridColumn: '2 / span 1' },
    '& .i3': { gridColumn: '3 / span 1' },
    '& .i4': { gridColumn: '4 / span 1' }
  }
}

function useFetchTableSemanticsStatis(
  dbTables: (TableSchemaResponseDto & RecPatch)[] | null, 
  database: DatabaseResponseDto, 
  dispatch: Dispatch<any>
) {
  const tbIdsRef = useRef({
    ids: null as number[] | null | undefined,
    timer: null as number | null,
    signature: '',
  });
  const tbIds = dbTables?.map(x => x.tableId!).sort();
  tbIdsRef.current.ids = tbIds;
  tbIdsRef.current.signature = JSON.stringify([database.databaseId, tbIds?.[0], tbIds?.[tbIds.length - 1]]);

  useEffect(() => {
    const tbIds = tbIdsRef.current.ids;
    if (!tbIds || tbIds.length === 0) { return; }

    let n = -1;
    const chunks = [...new Array(1 + Math.floor(tbIds.length / 100))].map((_, i) => i);
    tbIdsRef.current.timer = window.setInterval(() => {
      n = n + 1;
      const i = chunks[n];
      if (i == null) {
        clearInterval(tbIdsRef.current.timer!);
        tbIdsRef.current.timer = null;
        return;
      }
      const idChunk = tbIds.slice(100 * i, 100 * (i + 1));
      dispatch(crudGetList(tableSemanticStatisRes, pages(), sorts(), { databaseId: database.databaseId, tableIds: idChunk }));
    }, 100);

    return () => {
      if (tbIdsRef.current.timer) {
        clearInterval(tbIdsRef.current.timer);
        tbIdsRef.current.timer = null;
      }
    }
  }, [tbIdsRef.current.signature]);
}

